import EmailOpenIcon from "../../assets/svg/EmailOpenIcon";
import EmailSuccess from "../../assets/svg/EmailSuccess";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import VerificationCodeInput from "../../components/OtpInputBox/VerificationCodeInput";
import {
  resendOtpVerifyEmail,
  verifyEmail,
} from "../../endpoints/auth.service";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, setUserData } from "@store/actions/auth.action";
import { RootState } from "@store/reducers";

const EmailVerification = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate();
  const { state } = useLocation()
  const user = useSelector((state: RootState) => state?.auth?.user);
  const email = state?.email
  const { enqueueSnackbar } = useSnackbar();
  const [showInputBox, setShowInputBox] = useState(!user?.emailVerifiedAt);
  const [verifiedSuccess, setVerifiedSuccess] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);

  const RESEND_COOLDOWN_SECONDS = 30;

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  const handleResend = async () => {
    if (isResending || loading || resendCooldown > 0 || !email) return;
    setIsResending(true);
    setError("");
    try {
      const { success, message } = await resendOtpVerifyEmail(email);
      if (success) {
        enqueueSnackbar("A new verification code has been sent.", {
          variant: "success",
        });
        setCode("");
        setShowInputBox(true);
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      } else {
        setError(message || "Unable to resend code. Please try again.");
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message || "Unable to resend code.", {
        variant: "error",
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmitButton = async () => {
    if (loading) return;

    setLoading(true);
    setError("");

    try {
      if (verifiedSuccess) {
        if (user?.userType === 3) {
          navigate("/add-referral", { replace: true });
        } else {
          navigate(user?.userType ? "/login-ambassador" : "/login", {
            replace: true,
          });
        }
        return;
      }

      if (!showInputBox) {
        await resendOtpVerifyEmail(email);
        setShowInputBox(true);
      } else {
        const { data, success, message } = await verifyEmail({
          email: email,
          otpCode: code,
        });
        if (success) {
          if (data?.user?._id) {
            enqueueSnackbar("User verified successfully", { variant: "success" });
            setVerifiedSuccess(true);
            dispatch(loginUser(data))
          } else {
            enqueueSnackbar('Please wait for the recruiter admin approval.', { variant: "error" });
            navigate('/login', { replace: true })
          }
        } else {
          setError(
            message || "Verification failed. Please try again."
          );
        }
      }
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.emailVerifiedAt) {
      setShowInputBox(true);
    }
  }, [user?.emailVerifiedAt]);

  return (
    <div className="flex flex-col items-center justify-center rounded-xl flex-1 bg-white min-h-0">
      <span className="font-didact font-bold text-primary text-4xl">
        {verifiedSuccess ? "Verified!!" : "Email Verification"}
      </span>
      <span className="font-montserrat text-primary font-medium mt-3">
        {verifiedSuccess
          ? "Your account has been submitted for approval."
          : "Thanks for registering with us."}
      </span>
      {showInputBox && !verifiedSuccess ? null : verifiedSuccess ? (
        <EmailSuccess className={"w-14 h-14 my-7"} />
      ) : (
        <EmailOpenIcon className={"w-14 h-14 my-7"} />
      )}

      {showInputBox && !verifiedSuccess ? (
        <span className="font-montserrat text-light-black font-medium text-sm w-96 text-center">
          A verification code has been sent to your email address
          <span className="font-semibold"> {email}</span>, please check
          your inbox and insert the code to verify your email.
        </span>
      ) : verifiedSuccess ? (
        <span className="font-montserrat text-light-black font-medium text-sm w-96 text-center">
          Thank you, your email has been verified. Your account is now active.
          Please use the link below to login to your account.
        </span>
      ) : (
        <span className="font-montserrat text-light-black text-center">
          You’re almost ready to get started.<br></br> Please click on the button below
          to verify your email address.
        </span>
      )}

      {showInputBox && !verifiedSuccess && (
        <div className="mt-3">
          {/* @ts-ignore */}
          <VerificationCodeInput
            loading={loading}
            onChange={setCode}
            length={6}
          />
        </div>
      )}

      {error && (
        <span className="font-montserrat text-red-500 font-medium text-sm mt-2">
          {error}
        </span>
      )}

      <button
        disabled={(showInputBox && code?.length !== 6) || loading}
        onClick={handleSubmitButton}
        className={`font-montserrat font-medium text-sm text-white ${user?.userType ? "bg-primary" : "bg-secondary"
          } rounded-lg py-3 w-[28rem] mt-5 ${(showInputBox && code?.length !== 6) || loading
            ? "opacity-50"
            : "opacity-100"
          }`}
      >
        {loading
          ? "Processing..."
          : showInputBox && !verifiedSuccess
            ? "Verify"
            : verifiedSuccess
              ? user?.userType === 3
                ? "Proceed to your page"
                : "Login to your account"
              : "Verify your email address"}
      </button>

      {!verifiedSuccess && (
        <span className="font-montserrat mt-2.5 text-light-text">
          Didn't receive {showInputBox ? "the code" : "verification email"}?{" "}
          <span
            className={`text-secondary ${isResending || loading || resendCooldown > 0
                ? "opacity-50 cursor-not-allowed"
                : "cursor-pointer"
              }`}
            onClick={handleResend}
          >
            {isResending
              ? "Resending..."
              : resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : `Resend ${showInputBox ? "code" : ""}`}
          </span>
        </span>
      )}
    </div>
  );
};

export default EmailVerification;
